import {
  DownloadOutlined,
  ReloadOutlined,
  UserOutlined
} from "@ant-design/icons"
import {
  Avatar,
  Button,
  type CheckboxProps,
  Empty,
  Flex,
  Skeleton,
  Table,
  type TableColumnsType,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from "antd"
import dayjs from "dayjs"
import React, { useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import Icon from "../../Icon"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { DATE_FORMAT } from "../../utils/date/date.ts"
import { selectActiveBranchId } from "../Branches/redux/branches.slice.ts"
import { calendarDeepLink } from "../Calendar/helpers/calendar-deep-link.ts"
import GlobalMapListBulkActionBar from "./GlobalMapListBulkActionBar.tsx"
import { ListPaginationFooter } from "./ListPaginationFooter.tsx"
import { StatusPill } from "./StatusPill.tsx"
import { clearAllFilters } from "./helpers/clear-all-filters.ts"
import { buildFeatureId } from "./helpers/feature-id.ts"
import { getGlobalMapListColumnActions } from "./helpers/global-map-list-actions.tsx"
import { toDisplayRow } from "./helpers/to-display-row.ts"
import { useListFeatures } from "./hooks/useListFeatures.ts"
import { EntityIcon } from "./map/EntityIcon.tsx"
import {
  selectActiveFilters,
  selectActiveLayers,
  selectFeature,
  selectListDirection,
  selectListEntity,
  selectListSelection,
  selectListSort,
  selectSearchTerm,
  setListEntity,
  setListSelection,
  setListSort,
  setSearchTerm,
  toggleListDirection
} from "./redux/global-map.slice.ts"
import { exportListRows, fetchListRows } from "./redux/global-map.thunks.ts"
import type {
  ListEntityTab,
  ListRow,
  ListSortField,
  LocationState
} from "./redux/global-map.types.ts"

const { Text } = Typography

// The one sub-tab where a `status` sort would be ambiguous — Lead and
// Project statuses share one numeric id-space by coincidence (the same
// overlap `/facets` already documents), so the backend refuses `sort=status`
// whenever `list_entity=all` mixes both. The Status column's sort control is
// simply not offered on that sub-tab rather than letting a click send a
// request the server will 400.
const STATUS_SORT_DISABLED_TAB: ListEntityTab = "all"

// Only "approximate"/"missing" get a badge — "mapped" is the ordinary case
// and needs no visual call-out.
const LOCATION_BADGE_LABEL_KEY: Partial<Record<LocationState, string>> = {
  approximate: "globalMap.list.locationApproximate",
  missing: "globalMap.list.locationMissing"
}

// Task 14 — how many placeholder rows the loading skeleton shows. Not tied
// to `pageSize`: the skeleton renders before the first response has landed,
// so the real page size the request will come back with isn't known yet. A
// flat 10 matches the hook's own default `LIST_PAGE_SIZE`.
const SKELETON_ROW_COUNT = 10

interface SortableHeaderProps {
  field: ListSortField
  labelKey: string
  activeSort: ListSortField
  direction: "asc" | "desc"
  onSort: (field: ListSortField) => void
  disabled?: boolean
}

// A plain `<button>` inside the antd column title — antd's own built-in
// sorter UI (`sorter: true`/`onChange`) drives itself off column identity,
// not the List tab's own `ListSortField`/`toggleListDirection` slice
// contract (Task 10), so this reimplements just enough of "clickable header,
// shows the active direction" to stay wired to that contract instead.
//
// `aria-sort` deliberately does NOT live here: ARIA only supports it on a
// `columnheader`/`rowheader`/`gridcell`, and this button is a CHILD of the
// `<th>` antd generates. It's attached to that `<th>` through the column's
// own `onHeaderCell` instead (see `sortHeaderCellProps` below).
function SortableHeader({
  field,
  labelKey,
  activeSort,
  direction,
  onSort,
  disabled
}: SortableHeaderProps) {
  const { t } = useTranslation()
  const isActive = activeSort === field

  if (disabled) {
    return <span>{t(labelKey)}</span>
  }

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 bg-transparent p-0 font-medium"
      onClick={() => onSort(field)}
      data-testid={`global-map-list-sort-${field}`}
    >
      {t(labelKey)}
      {isActive && (
        <span aria-hidden="true">{direction === "asc" ? "↑" : "↓"}</span>
      )}
    </button>
  )
}

// Task 14 — replaces the generic `MapNoResults` this tab used to share with
// the Map tab's own empty/error states. Both states below get the List
// tab's own card treatment (`rounded-lg border border-gray-200 bg-white`,
// matching the table card further down) rather than a bare `Empty`/`Result`
// floating on the page background.
function ListEmptyState({ onClearFilters }: { onClearFilters: () => void }) {
  const { t } = useTranslation()

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={12}
      className="min-h-[320px] flex-1 rounded-lg border border-gray-200 bg-white p-6 text-center"
    >
      <Text type="secondary" strong>
        {t("globalMap.list.states.empty.title")}
      </Text>
      <Button onClick={onClearFilters}>
        {t("globalMap.list.states.empty.clearFilters")}
      </Button>
    </Flex>
  )
}

// The Retry action deliberately takes the same `handleRetry` the tab already
// had — it re-issues the current query untouched (same filters/search/sort/
// sub-tab), so a Retry click never silently discards what the user had
// filtered on. Only the copy/container changed here, not that behavior.
function ListErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={12}
      className="min-h-[320px] flex-1 rounded-lg border border-gray-200 bg-white p-6 text-center"
    >
      <Text type="secondary" strong>
        {t("globalMap.list.states.error.title")}
      </Text>
      <Button icon={<ReloadOutlined />} onClick={onRetry}>
        {t("globalMap.list.states.error.retry")}
      </Button>
    </Flex>
  )
}

/**
 * The List tab's content — the sortable, page-number-paginated table counterpart
 * to `MapTab`'s canvas. Row selection dispatches the identical `selectFeature`
 * action `MapTab.handleSelectFeature` does, via the same `buildFeatureId`
 * helper, so switching back to the Map tab shows the same record already
 * selected — "synchronized" is a consequence of both tabs reading/writing
 * one shared piece of Redux state, not a second mechanism built to imitate
 * it. Mounted by `GlobalMapPage`/`MapTab` (Task 6, dispatched after this
 * task) when `activeTab === "list"` — this component has no opinion about
 * that and renders standalone.
 *
 * Task 12 replaced the prior 7-column, mostly-placeholder table with the
 * target 8-column model (Type/Name/Location/Status/Owner/Date/Value/
 * Actions — Selection is antd's own `rowSelection`, not a manual column),
 * rendering every column from `toDisplayRow(row, t)`'s normalized
 * `GlobalMapListDisplayRow` rather than branching on entity shape inline.
 */
interface ListTabProps {
  // Threaded from `MapTab`'s own `useGlobalMapUrlSync()` — the real "was
  // `?pageSize=` present on the URL at mount" signal `useListFeatures` needs
  // to resolve the URL-vs-persisted-preference precedence correctly. See
  // `useGlobalMapUrlSync.ts`'s own doc comment on its `pageSizeFromUrl`
  // state for why a value comparison alone can't stand in for this.
  pageSizeFromUrl?: number | null
  // Fix (review round) — also threaded from `MapTab`'s own `isUrlSynced`
  // (`useGlobalMapUrlSync()`'s `isReady`), gating `useListFeatures`'s mount
  // effect the same way `MapTab` already gates `useMapFeatures`/
  // `useGlobalMapFacets` on it. Without this, a `?tab=list` deep link can
  // mount `ListTab` (Redux's `activeTab` commits) in an EARLIER render than
  // `pageSizeFromUrl` itself resolves from `null` to its real value —
  // confirmed via a real render-order trace, not assumed — so the mount
  // effect would fire once, prematurely, against the still-`null`
  // `pageSizeFromUrl`, permanently missing the precedence fix above (the
  // effect only ever runs once). Defaults `true` so every existing direct
  // `<ListTab/>` render (this component's own tests, which mount it
  // standalone with no `MapTab`/router above it) behaves exactly as before.
  isUrlSynced?: boolean
  // "Show on Map" bulk action — owned by `MapTab` (the fetch/camera-fit/
  // tab-switch orchestration all live there, see `useMapFeatures.
  // showFeaturesByIds`'s own doc comment for why), threaded down the same
  // way `onViewAll`/`onSelectSearchResult` already are for
  // `GlobalMapToolbar`. Optional so this component's own standalone tests
  // (which render it with no `MapTab` above it) don't need to supply one —
  // the button simply disables itself when absent.
  onShowOnMap?: (ids: string[]) => void | Promise<void>
}

const ListTab = ({
  pageSizeFromUrl = null,
  isUrlSynced = true,
  onShowOnMap
}: ListTabProps = {}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const listEntity = useAppSelector(selectListEntity)
  const sort = useAppSelector(selectListSort)
  const direction = useAppSelector(selectListDirection)
  const filters = useAppSelector(selectActiveFilters)
  const searchTerm = useAppSelector(selectSearchTerm)
  const activeLayers = useAppSelector(selectActiveLayers)
  const activeBranchId = useAppSelector(selectActiveBranchId) ?? undefined
  const selectedIds = useAppSelector(selectListSelection)
  // Optional chaining throughout: this component's own tests mount it over
  // a bare `{ globalMap, uiPreferences }` store with no `auth` slice at all
  // — the real app always has one, but nothing here should crash without
  // it. Mirrors `getProjectsColumnActions`' `editAllowed` — Project's own
  // existing `project_edit` right, no new permission invented.
  const projectEditAllowed = useAppSelector(
    (state) =>
      !!state.auth?.permissions?.codes?.projects?.projects?.project_edit
  )

  const { data, status, currentPage, setPage, pageSize, setPageSize } =
    useListFeatures(isUrlSynced, pageSizeFromUrl)

  const [isExporting, setIsExporting] = useState(false)
  const [isShowingOnMap, setIsShowingOnMap] = useState(false)

  // Real bug fix, reported three times before this landed: without a
  // measured `scroll.y`, a full page of rows (20/page, 50/page) overflowed
  // the card's `overflow-hidden` with no scrollbar anywhere to recover the
  // pagination footer or the bulk-action bar.
  //
  // This does NOT use the shared `useMeasuredTableHeight`/
  // `useFittedTableHeight` hooks every other scrolled table in this app
  // uses — both of that hook's modes were live-tested against a real
  // browser and found unreliable for THIS specific page's layout:
  //
  // - `fillsViewportBelow` (the `LeadsListTable.tsx` precedent for this
  //   exact symptom, EAD-418) OVERSHOT: its formula assumes only a fixed
  //   sticky header sits above the scrolled-to card — true for Leads'
  //   page shape, not here, where the KPI tiles/toolbar/filter bar above
  //   this table are permanently visible chrome, not content that scrolls
  //   away. Measured live: the table body rendered ~1001px tall, ending
  //   344px past the real window bottom, clipped by this card's own
  //   `overflow-hidden`.
  // - Plain `fullPage: true` worked in one live test (exactly 1536x1024)
  //   but under-filled available space badly (~154px, 3 rows) — and a
  //   second live report at a different real window size showed it
  //   STILL not reaching the footer, meaning `reachableBottom()`'s
  //   `window.innerHeight` fallback is not a reliable proxy for this
  //   page's real available height either (this page's app shell,
  //   `CustomLayout`'s `#main-content`, has real `overflow-y: auto` but
  //   no CSS-authored `max-height`, so the hook's ancestor walk can never
  //   find a trustworthy bound here — see `use-fitted-table-height.ts`'s
  //   own `reachableBottom` for why it needs one).
  //
  // Given neither mode of the shared hook can find a real bound on this
  // page, this measures directly and simply instead: this card's own
  // real `top` position (which already reflects however much chrome
  // — KPI tiles, toolbar, filters, tabs — actually rendered above it,
  // whatever that turns out to be) plus a fixed, generous reserve for
  // what's rendered below it (the pagination footer and, once a row is
  // selected, the bulk-action bar) and a safety margin. Recomputed on
  // resize and after each render (rows loading can shift the card's own
  // position slightly, e.g. via filter chips wrapping to a second line).
  const tableRef = useRef<HTMLDivElement>(null)
  const [tableHeight, setTableHeight] = useState(400)

  useLayoutEffect(() => {
    const measure = () => {
      const top = tableRef.current?.getBoundingClientRect().top
      if (top == null) return
      // Reserve: pagination footer (~57px) + bulk-action bar when visible
      // (~65px) + the gap between them + a safety margin — generous on
      // purpose, since under-filling by a row or two is a minor cosmetic
      // cost while overshooting is the exact bug this exists to prevent.
      const RESERVE_BELOW = 170
      const MIN_HEIGHT = 200
      const available = window.innerHeight - top - RESERVE_BELOW
      setTableHeight(Math.max(MIN_HEIGHT, Math.round(available)))
    }

    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  })

  // Fix (review round) — reinstated: a rapid page/page-size change while a
  // PRIOR request is still resolving would otherwise fire a second,
  // redundant concurrent fetch (the original dropped-Button guard's real
  // job; data correctness itself was already safe via the slice's own
  // `requestId` staleness guard, but the extra request is wasted work).
  // "loading" (first fetch) and "refreshing" (any fetch after) both mean a
  // request is currently in flight — mirrors the old Previous/Next
  // `<Button disabled={...}>`'s own condition before `ListPaginationFooter`
  // replaced them.
  const requestInFlight = status === "loading" || status === "refreshing"

  const handleRowSelect = (row: ListRow) => {
    // Row selection/quick-view is Project/Lead-only — `buildFeatureId`/
    // `parseFeatureId` both key on the map's own `EntityType`
    // ("project"/"lead" only). Subcontractor/Appointment have no
    // namespaced-id counterpart, and no quick-view backing them yet —
    // selecting one of these rows is a safe no-op.
    if (row.entity_type !== "project" && row.entity_type !== "lead") return
    const { entity_type: entityType, id } = row
    dispatch(selectFeature({ id: buildFeatureId(entityType, id), entityType }))
  }

  // Where a row's "View details" (and Project's "Edit") menu item
  // navigates — real existing routes only, never invented:
  // - Lead: `RoutesPaths.lead.view` (the Lead Workspace itself; there is no
  //   separate edit route — see `getGlobalMapListColumnActions`'s own
  //   comment).
  // - Project: `RoutesPaths.project.view(...).overview` / `.edit`, same as
  //   `project-row-action-route.ts`.
  // - Subcontractor: `RoutesPaths.subcontractors` — the Subcontractors page
  //   has no per-id route at all; its detail view is a Redux-state-driven
  //   modal opened from within that page, not a URL. Landing on the list is
  //   the honest "real existing route" for this entity.
  // - Appointment: the Calendar's own `calendarDeepLink` (`?event=<id>`),
  //   the same deep link `notification-deep-links.ts` and the header's Edit
  //   action already use.
  const handleRowAction = (row: ListRow) => {
    switch (row.entity_type) {
      case "lead":
        navigate(RoutesPaths.lead.view(row.id))
        return
      case "project":
        navigate(RoutesPaths.project.view(row.id).overview)
        return
      case "subcontractor":
        navigate(RoutesPaths.subcontractors.list)
        return
      case "appointment":
        navigate(
          calendarDeepLink(dayjs(row.start_at).format(DATE_FORMAT), row.id)
        )
        return
    }
  }

  const handleEditProject = (row: ListRow) => {
    if (row.entity_type !== "project") return
    navigate(RoutesPaths.project.edit(row.id))
  }

  const handleSortClick = (field: ListSortField) => {
    if (field === sort) {
      dispatch(toggleListDirection())
    } else {
      dispatch(setListSort(field))
    }
  }

  // The terminal-error empty state's own retry — `useListFeatures` exposes
  // no manual refetch, so this re-issues the request for the page/size
  // currently held in state, using the same selectors it reads internally.
  // Fix (final-review Important #2) — this used to hardcode `page: 1` while
  // `pageSize` already came from `useListFeatures`'s own `currentPage`/
  // `pageSize`, the same page/state-desync shape as `refetchCurrentListOrMapQuery`
  // (Important #1): in practice `currentPage` is almost always 1 already
  // here (this button only renders from `status === "error" && !data`), but
  // reading `currentPage` instead of hardcoding keeps the two fixes
  // consistent and removes the latent bug for whenever that stops being
  // true.
  const handleRetry = () => {
    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: currentPage,
        pageSize
      })
    )
  }

  // Task 14's empty-state "Clear Filters" — calls the SAME shared
  // `clearAllFilters` function `GlobalMapFilterBar`'s own Clear All button
  // (Task 6) dispatches through, rather than re-listing the same sequence
  // independently (review-round fix: the two started out behaviorally
  // equivalent but were two separate implementations that could silently
  // diverge). `includeListReset: true` always, unlike the toolbar's own
  // `activeTab === "list"` guard — this button only exists while the List
  // tab is already showing. Search-term clearing stays here, not in the
  // shared helper — see that function's own doc comment for why.
  const handleClearFilters = () => {
    clearAllFilters(dispatch, activeLayers, { includeListReset: true })
    dispatch(setSearchTerm(""))
  }

  // Selection wins over "everything the current filters match" (the spec's
  // own stated precedence, Task 13) — `undefined` (never an empty array)
  // when nothing is selected, so `exportListRows`'s own `ExportQueryParams`
  // omits `ids` entirely rather than sending a meaningless empty one.
  const handleExport = async () => {
    setIsExporting(true)
    try {
      await dispatch(
        exportListRows({
          filters,
          search: searchTerm,
          entities: activeLayers,
          team: activeBranchId,
          listEntity,
          sort,
          direction,
          ids: selectedIds.length ? selectedIds : undefined
        })
      )
    } finally {
      setIsExporting(false)
    }
  }

  // "Show on Map" — Project/Lead-only, same restriction `handleRowSelect`
  // above already applies (Subcontractor/Appointment rows have no map
  // marker/feature-id counterpart at all). A mixed selection silently drops
  // the ineligible rows rather than refusing the whole action — mirrors
  // `handleRowSelect`'s own silent no-op for a single ineligible row.
  const showOnMapEligibleIds = selectedIds.filter((id) => {
    const row = data?.rows.find((r) => r.id === id)
    return row?.entity_type === "project" || row?.entity_type === "lead"
  })

  const handleShowOnMap = async () => {
    if (!onShowOnMap || !showOnMapEligibleIds.length) return
    setIsShowingOnMap(true)
    try {
      await onShowOnMap(showOnMapEligibleIds)
    } finally {
      setIsShowingOnMap(false)
    }
  }

  if (status === "error" && !data) {
    return <ListErrorState onRetry={handleRetry} />
  }

  if (data && data.total === 0) {
    return <ListEmptyState onClearFilters={handleClearFilters} />
  }

  // The first fetch of a mount/deep-link — no response has landed for THIS
  // query yet, so there is nothing real to show. Distinct from `status ===
  // "refreshing"` (a later fetch, e.g. a sort/page change, where the PRIOR
  // page's rows stay on screen under `Table`'s own `loading` spinner
  // overlay instead) — this one instead renders `SKELETON_ROW_COUNT`
  // placeholder rows in the table body, at the real columns' widths, so the
  // page's own layout (tabs, card, pagination footer) never collapses to a
  // full-page spinner and back.
  const isInitialLoading = status === "loading" && !data

  // ARIA only honours `aria-sort` on the column header cell itself, so it's
  // attached to the `<th>` antd generates rather than to the `SortableHeader`
  // button rendered inside it.
  const sortHeaderCellProps = (field: ListSortField) => () => ({
    "aria-sort": (sort === field
      ? direction === "asc"
        ? "ascending"
        : "descending"
      : "none") as React.AriaAttributes["aria-sort"]
  })

  const columns: TableColumnsType<ListRow> = [
    {
      title: t("globalMap.list.columnType"),
      dataIndex: "entity_type",
      key: "entity_type",
      width: 135,
      render: (_: unknown, row: ListRow) => (
        <span className="flex items-center gap-1.5">
          {(row.entity_type === "project" || row.entity_type === "lead") && (
            <EntityIcon entityType={row.entity_type} className="size-4" />
          )}
          {t(`globalMap.entityLabel.${row.entity_type}`)}
        </span>
      )
    },
    {
      title: (
        <SortableHeader
          field="name"
          labelKey="globalMap.list.columnName"
          activeSort={sort}
          direction={direction}
          onSort={handleSortClick}
        />
      ),
      key: "name",
      width: 220,
      onHeaderCell: sortHeaderCellProps("name"),
      render: (_: unknown, row: ListRow) => {
        const display = toDisplayRow(row, t)
        return (
          <div className="flex flex-col">
            <span className="font-medium">{display.name}</span>
            {display.secondaryText && (
              <span className="text-xs text-gray-500">
                {display.secondaryText}
              </span>
            )}
          </div>
        )
      }
    },
    {
      title: t("globalMap.list.columnLocation"),
      key: "location",
      width: 220,
      render: (_: unknown, row: ListRow) => {
        const display = toDisplayRow(row, t)
        const hasNoAddress = !display.locationPrimary
        // A missing location gets no pin icon (the icon signals a real,
        // mapped address, which contradicts the message) and no separate
        // "Missing location" badge — that would just repeat what the red
        // text already says. An approximate (but present) location keeps
        // both, since its badge adds real information the primary text
        // doesn't already carry.
        const badgeKey = hasNoAddress
          ? undefined
          : LOCATION_BADGE_LABEL_KEY[display.locationState]
        return (
          <div className="flex flex-col gap-1">
            <Flex gap={4} align="center">
              {!hasNoAddress && (
                <Icon type="Map" className="shrink-0 text-gray-400" />
              )}
              <div className="flex flex-col">
                <span
                  className={hasNoAddress ? "ml-1 text-red-500" : undefined}
                >
                  {display.locationPrimary ?? t("globalMap.list.noAddress")}
                </span>
                {display.locationSecondary && (
                  <span className="text-xs text-gray-500">
                    {display.locationSecondary}
                  </span>
                )}
              </div>
            </Flex>
            {badgeKey && (
              <Tag data-testid={`global-map-list-location-badge-${row.id}`}>
                {t(badgeKey)}
              </Tag>
            )}
          </div>
        )
      }
    },
    {
      title: (
        <SortableHeader
          field="status"
          labelKey="globalMap.list.columnStatus"
          activeSort={sort}
          direction={direction}
          onSort={handleSortClick}
          disabled={listEntity === STATUS_SORT_DISABLED_TAB}
        />
      ),
      key: "status_label",
      width: 150,
      // No sort control is offered on the "all" sub-tab, so the header cell
      // carries no sort state to announce either.
      onHeaderCell:
        listEntity === STATUS_SORT_DISABLED_TAB
          ? undefined
          : sortHeaderCellProps("status"),
      render: (_: unknown, row: ListRow) => (
        <StatusPill
          entityType={row.entity_type}
          statusLabel={row.status_label}
        />
      )
    },
    // Task 15 — NOT extended with `SortableHeader`, on purpose: the brief's
    // own gate is "where a meaningful sort exists", and Owner has no such
    // sort today. `ListSortField` (`global-map.types.ts`) is exactly
    // `"relevant_date" | "name" | "status"`, and the backend enforces the
    // same set at the wire level — `ezbuild_back`'s
    // `apps/global_map/list_pagination.py` `SORT_FIELDS` dict has no
    // `owner`/salesperson/project_manager entry, and `serializers.py`'s
    // `sort = serializers.ChoiceField(choices=list(SORT_FIELDS.keys()), ...)`
    // 400s any other value — confirmed by reading both files rather than
    // assumed. A clickable "sort by Owner" control would be a real, new bug
    // (a control whose every click 400s), not an accessibility gap this
    // task can close without a backend contract change. Same STATUS_SORT_
    // DISABLED_TAB-style "there is genuinely nothing to wire up" reasoning
    // as the Status column's own disabled state above — see this task's own
    // report for the full writeup.
    {
      title: t("globalMap.list.columnOwner"),
      key: "owner",
      width: 190,
      render: (_: unknown, row: ListRow) => {
        const { owner } = toDisplayRow(row, t)
        if (!owner) return <Text type="secondary">—</Text>

        return (
          <Flex gap={8} align="center">
            <Avatar
              className="shrink-0"
              size="small"
              src={owner.avatarUrl}
              icon={!owner.avatarUrl && <UserOutlined />}
            />
            <div className="flex flex-col">
              <span>{owner.name}</span>
              {owner.role && (
                <span className="text-xs text-gray-500">{owner.role}</span>
              )}
            </div>
          </Flex>
        )
      }
    },
    {
      title: (
        <SortableHeader
          field="relevant_date"
          labelKey="globalMap.list.columnDate"
          activeSort={sort}
          direction={direction}
          onSort={handleSortClick}
        />
      ),
      key: "relevant_date",
      width: 160,
      onHeaderCell: sortHeaderCellProps("relevant_date"),
      render: (_: unknown, row: ListRow) => {
        const display = toDisplayRow(row, t)
        return (
          <div className="flex flex-col">
            <span>{display.datePrimary}</span>
            <span className="text-xs text-gray-500">{display.dateLabel}</span>
          </div>
        )
      }
    },
    // Task 15 — same reasoning as Owner above, doubly so for Value: the
    // brief scopes a numeric sort to "Lead/Project only, where the
    // underlying field is truly numeric", but `to-display-row.ts`'s own
    // `projectOrLeadDisplayRow` sets `valuePrimary: undefined` for EVERY
    // Lead/Project row today — confirmed there's no estimate/contract-value
    // field on `ListRow` at all yet (that function's own doc comment names
    // it as a backend addition this frontend-only worktree can't add). A
    // Lead/Project's Value column has no real value to sort BY yet, let
    // alone a backend `sort=value` to request it with — the same
    // `SORT_FIELDS`/`ChoiceField` contract cited above has no `value` entry
    // either. Subcontractor/Appointment's Value cell is confirmed
    // text-shaped (active-project count / related-record title), matching
    // the brief's own carve-out for them.
    {
      title: t("globalMap.list.columnValue"),
      key: "value",
      width: 170,
      render: (_: unknown, row: ListRow) => {
        const display = toDisplayRow(row, t)
        return (
          <div className="flex flex-col">
            <span>{display.valuePrimary ?? "—"}</span>
            <span className="text-xs text-gray-500">{display.valueLabel}</span>
          </div>
        )
      }
    },
    getGlobalMapListColumnActions({
      t,
      editAllowed: projectEditAllowed,
      onSelectRow: (row, action) => {
        if (action === "edit") {
          handleEditProject(row)
        } else {
          handleRowAction(row)
        }
      }
    })
  ]

  // Task 14 — the loading skeleton reuses the real columns' `width`s/keys so
  // the table's footprint doesn't shift once real rows replace them; only
  // the cell content becomes a placeholder bar. The header keeps its real
  // title (and sort control) rather than also becoming a placeholder — it's
  // already meaningful before any row has loaded.
  const skeletonColumns: TableColumnsType<ListRow> = columns.map((column) => ({
    ...column,
    render: () => (
      <Skeleton.Input active size="small" block style={{ height: 16 }} />
    )
  }))

  // Cast: a skeleton row only ever reaches `skeletonColumns`' own `render`
  // above, which ignores the row entirely — `id` is the only field `rowKey`/
  // `onRow` below need from it.
  const skeletonRows = Array.from(
    { length: SKELETON_ROW_COUNT },
    (_, index) => ({
      id: `skeleton-row-${index}`
    })
  ) as ListRow[]

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3"
      data-testid="global-map-list-tab"
    >
      <Tabs
        activeKey={listEntity}
        onChange={(key) => dispatch(setListEntity(key as ListEntityTab))}
        tabBarExtraContent={
          <Button
            icon={<DownloadOutlined />}
            loading={isExporting}
            disabled={status !== "idle" || (data?.total ?? 0) === 0}
            onClick={handleExport}
            data-testid="global-map-list-export-button"
            className="h-9"
          >
            {t("globalMap.list.export.button")}
          </Button>
        }
        items={[
          {
            key: "all",
            label: (
              <span data-testid="global-map-list-tab-all">
                {t("globalMap.list.all")}{" "}
                <span data-testid="global-map-list-count-all">
                  {/* Real bug (reported): `data.total` is scoped to
                      whichever sub-tab is currently active (11 while
                      viewing Projects, not the true unfiltered count), so
                      using it here made the "All" label silently mirror
                      whatever sub-tab you'd just clicked into instead of
                      staying fixed. The "All" sub-tab's own real population
                      is Project+Lead only (the backend's `list_entity=all`
                      branch scopes to `ALL_ENTITIES = {"project", "lead"}`
                      — Subcontractor/Appointment are deliberately never
                      part of "All", they're their own separate sub-tabs),
                      and `project_total`/`lead_total` are always fresh,
                      entities-toggle-aware, and identical across every
                      sub-tab's response — the same stability
                      `columnLocation`'s sibling counts below already rely
                      on — so their sum is the one value that can't drift
                      with whichever tab happens to be open. */}
                  {(data?.project_total ?? 0) + (data?.lead_total ?? 0)}
                </span>
              </span>
            )
          },
          {
            key: "project",
            label: (
              <span data-testid="global-map-list-tab-project">
                <Icon type="Projects" style={{ color: "#16a34a" }} />{" "}
                {t("globalMap.list.projects")}{" "}
                <span data-testid="global-map-list-count-project">
                  {data?.project_total ?? 0}
                </span>
              </span>
            )
          },
          {
            key: "lead",
            label: (
              <span data-testid="global-map-list-tab-lead">
                <Icon type="Leads" style={{ color: "#1677ff" }} />{" "}
                {t("globalMap.list.leads")}{" "}
                <span data-testid="global-map-list-count-lead">
                  {data?.lead_total ?? 0}
                </span>
              </span>
            )
          },
          {
            key: "subcontractor",
            label: (
              <span data-testid="global-map-list-tab-subcontractor">
                <Icon type="Subcontractors" style={{ color: "#9333ea" }} />{" "}
                {t("globalMap.list.subcontractors")}{" "}
                <span data-testid="global-map-list-count-subcontractor">
                  {data?.subcontractor_total ?? 0}
                </span>
              </span>
            )
          },
          {
            key: "appointment",
            label: (
              <span data-testid="global-map-list-tab-appointment">
                <Icon type="Calendar" style={{ color: "#0ea5e9" }} />{" "}
                {t("globalMap.list.appointments")}{" "}
                <span data-testid="global-map-list-count-appointment">
                  {data?.appointment_total ?? 0}
                </span>
              </span>
            )
          },
          {
            key: "workforce",
            disabled: true,
            label: (
              <Tooltip title={t("globalMap.list.workforceComingSoon")}>
                <span data-testid="global-map-list-tab-workforce">
                  {t("globalMap.list.workforce")}
                </span>
              </Tooltip>
            )
          }
        ]}
      />

      {selectedIds.length > 0 && (
        <span
          className="text-xs font-medium text-gray-500"
          data-testid="global-map-list-selection-summary"
        >
          {t("globalMap.list.selectedCount", { count: selectedIds.length })}
        </span>
      )}

      {/* Task 14 — the table card: rounded corners + a light border around
          the table AND its pagination footer (the footer already carried
          its own `border-t`, which is what this card completes rather than
          duplicates). `overflow-hidden` clips the table's square corners to
          the card's rounded ones. Row dimensions (Task 17 visual-validation
          pass — corrected from an earlier ~68px estimate after pixel-
          measuring the actual target screenshot's row spacing, ~54px):
          ~48px header row, ~56px body row, 16px horizontal / 8px vertical
          cell padding — applied as descendant overrides below since antd's
          own `Table` exposes no height/padding props for this.

          Real bug (reported repeatedly): with no `scroll.y`, antd never
          renders a scrollable `.ant-table-body` — a full page of rows
          (20/page, 50/page) just overflows this card's own
          `overflow-hidden`, which clips it, taking the pagination footer
          AND the bulk-action bar below it out of reach with no scrollbar
          anywhere on the page to recover them. `tableHeight` (computed
          just above, see that state's own doc comment for why this
          doesn't use the shared `useMeasuredTableHeight` hook every other
          scrolled table in this app uses) feeds `scroll.y`, so the table
          body itself scrolls internally once its rows exceed the real
          space this card has, while the header, footer and bulk-action
          bar all stay on-screen. */}
      <div
        ref={tableRef}
        className="flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white"
      >
        <Table<ListRow>
          dataSource={isInitialLoading ? skeletonRows : (data?.rows ?? [])}
          columns={isInitialLoading ? skeletonColumns : columns}
          rowKey="id"
          pagination={false}
          loading={status === "refreshing"}
          scroll={{ x: "max-content", y: tableHeight }}
          className="[&_.ant-table-cell]:px-4 [&_.ant-table-cell]:py-2 [&_.ant-table-thead>tr>th]:h-12 [&_.ant-table-tbody>tr>td]:h-14"
          locale={{
            emptyText: (
              <Empty description={t("globalMap.states.noResults.title")} />
            )
          }}
          rowSelection={{
            selectedRowKeys: isInitialLoading ? [] : selectedIds,
            onChange: isInitialLoading
              ? () => {}
              : (keys) => dispatch(setListSelection(keys as string[])),
            // Task 15 — a generic "select row" gives a screen-reader user no
            // way to tell rows apart by ear; naming it from the same
            // `toDisplayRow(row, t).name` the row's own `aria-label` already
            // uses (below) keeps the two labels for one record consistent.
            // Skeleton rows carry no real record, so they stay unlabeled
            // (and already `disabled`, which is what actually matters for
            // them — they're also `aria-hidden` at the row level).
            //
            // `getCheckboxProps`'s own TS type omits `React.AriaAttributes`
            // (unlike `getTitleCheckboxProps` below, which is typed to
            // include it) — this annotates the callback's return type
            // explicitly rather than casting the object, since antd's
            // `Checkbox` spreads unrecognised props straight onto the
            // underlying `rc-checkbox`/native `<input>` at runtime either
            // way (confirmed by reading `antd/es/checkbox/Checkbox.js`).
            getCheckboxProps: (
              row: ListRow
            ): Partial<Omit<CheckboxProps, "checked" | "defaultChecked">> &
              React.AriaAttributes =>
              isInitialLoading
                ? { disabled: true }
                : {
                    "aria-label": t("globalMap.list.selectRow", {
                      name: toDisplayRow(row, t).name
                    })
                  },
            getTitleCheckboxProps: () => ({
              "aria-label": t("globalMap.list.selectAllRows")
            }),
            columnWidth: 44
          }}
          onRow={
            isInitialLoading
              ? (row) => ({
                  // Placeholder content, not a real record — not a tab
                  // stop, and hidden from assistive tech the same way the
                  // rest of a loading skeleton usually is.
                  tabIndex: -1,
                  "aria-hidden": true,
                  "data-testid": `global-map-list-skeleton-row-${row.id}`
                })
              : (row) => ({
                  onClick: () => handleRowSelect(row),
                  // `onRow`'s returned props spread onto a plain `<tr>` —
                  // antd/rc-table gives it zero keyboard/ARIA behavior of
                  // its own (a row is only reachable by pointer otherwise),
                  // so Enter/Space activation is added by hand, the same
                  // way `SortableHeader` above gets it for free from being
                  // a native `<button>`. `role` is deliberately left alone
                  // (not overridden to "button") so the row still announces
                  // as a table row — `aria-label` names the record instead,
                  // so it also reads as identifiable and actionable without
                  // fighting the table's own row semantics.
                  onKeyDown: (
                    event: React.KeyboardEvent<HTMLTableRowElement>
                  ) => {
                    // Task 15 fix — a real keyboard-trap this task's own
                    // menu test caught: the row itself carries two
                    // interactive descendants (the selection checkbox, the
                    // row-actions button), and Enter/Space on EITHER one
                    // bubbles up through this same handler regardless of
                    // where it started — which was calling
                    // `preventDefault()` before that descendant's own native
                    // Enter-triggers-click behavior could run (browsers, and
                    // `@testing-library/user-event`'s own keyboard model,
                    // only dispatch that click on an UNprevented keydown),
                    // so the row-actions menu could never be opened via the
                    // keyboard at all. Scoping to
                    // `event.target === event.currentTarget` restricts this
                    // to a genuine row-level press — the event actually
                    // originating on the `<tr>`, not merely bubbling through
                    // it — leaving the checkbox/menu button free to handle
                    // their own Enter/Space exactly the way a native control
                    // already does.
                    if (event.target !== event.currentTarget) return
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault()
                      handleRowSelect(row)
                    }
                  },
                  tabIndex: 0,
                  "aria-label": toDisplayRow(row, t).name,
                  "data-testid": `global-map-list-row-${row.id}`,
                  className: "cursor-pointer"
                })
          }
        />

        <ListPaginationFooter
          total={data?.total ?? 0}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          disabled={requestInFlight}
        />
      </div>

      <GlobalMapListBulkActionBar
        selectedCount={selectedIds.length}
        showOnMapDisabled={!onShowOnMap || !showOnMapEligibleIds.length}
        showOnMapLoading={isShowingOnMap}
        onShowOnMap={handleShowOnMap}
        onClear={() => dispatch(setListSelection([]))}
      />
    </div>
  )
}

export default ListTab
